'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Trash2, Image } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { formatTime } from '@/lib/utils';
import { DialogTitle } from '@radix-ui/react-dialog';

type ImageTable = {
  image_id: string;
  appointment_id: string;
  user_id: string;
  image_url: string;
  created_at?: string;
  project_id?: string;
};

interface ImageGalleryProps {
  images: ImageTable[];
  onDeleteImage?: (imageId: string) => Promise<void>;
}

export default function ImageGallery({ images = [], onDeleteImage }: ImageGalleryProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [selectedImage, setSelectedImage] = useState<ImageTable | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState<ImageTable | null>(null);

  const handleDeleteImage = async () => {
    if (imageToDelete) {
      try {
        if (!onDeleteImage) {
          throw new Error('onDeleteImage function is not provided');
        }
        await onDeleteImage(imageToDelete.image_id);
        toast({
          title: t('imageDeleted'),
          description: t('imageDeletedDescription'),
          variant: 'default',
        });
      } catch (error) {
        console.error('Error deleting image:', error);
        toast({
          title: t('errorDeletingImage'),
          description: t('errorDeletingImageDescription'),
          variant: 'destructive',
        });
      }
    }
    setIsDeleteDialogOpen(false);
    setImageToDelete(null);
  };

  return (
    <>
      <Dialog>
        <DialogTrigger asChild>
          <Button>
            <Image className="h-6 w-6 mr-2" />
            {t('photos')}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-screen-md w-full p-4 sm:p-6">
          <DialogTitle className="text-lg font-bold">{t('images')}</DialogTitle>

          <div className="grid gallery h-72 overflow-y-auto">
            {images.map((image) => (
              <div key={image.image_id} className="relative aspect-square">
                <div className=" inset-0 bg-black bg-opacity-0  transition-opacity items-center duration-200 flex  gap-1 p-2">
                  {onDeleteImage != undefined && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="self-end text-red-500"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImageToDelete(image);
                        setIsDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">{t('deleteImage')}</span>
                    </Button>
                  )}
                  <p className="text-xs text-white bg-black bg-opacity-50 p-1 rounded">
                    {image.created_at ? formatTime(image.created_at) : t('noDate')}
                  </p>
                </div>
                <img
                  src={image.image_url}
                  alt={t('galleryImage', { id: image.image_id })}
                  className="rounded-lg w-full h-full cursor-pointer border border-border"
                  onClick={() => setSelectedImage(image)}
                />
              </div>
            ))}
          </div>

          <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('confirm')}</AlertDialogTitle>
                <AlertDialogDescription>{t('deleteconfirm')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteImage}>
                  {t('delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </DialogContent>
      </Dialog>
      <Dialog open={selectedImage != null} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent>
          {selectedImage && (
            <img
              src={selectedImage.image_url}
              className="rounded-md"
              alt={t('selectedImage')}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
